import React, { Ref, useCallback, useEffect, useRef, useState } from 'react';
import { Input as AntInput, InputRef } from 'antd';
import { SizeType } from 'antd/lib/config-provider/SizeContext';
import { useInput, UseInputParams } from 'utils/hooks/useInput';

export interface InputProps extends UseInputParams<string, HTMLInputElement> {
  id?: string;
  name?: string;
  value?: string;
  initialValue?: string;
  placeholder?: string;
  onPressEnter?: (value: string) => void;
  maxValue?: number;
  size?: SizeType;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  bordered?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  ellipsis?: boolean;
  maxLength?: number;
  color?: string;
  passRef?: Ref<InputRef>;
  className?: string;
  showCount?: boolean;
  onKeyPress?: React.KeyboardEventHandler<HTMLInputElement>;
  onInput?: React.FormEventHandler<HTMLInputElement>;
  onPaste?: React.ClipboardEventHandler<HTMLInputElement>;
  status?: 'warning' | 'error';
  addonBefore?: React.ReactNode;
  addonAfter?: React.ReactNode;
  allowClear?: boolean;
  autoComplete?: string;
  'data-cy'?: string;
}

export interface CleverInputProps extends InputProps {
  validateInput?: (val: string) => boolean;
}

export const CleverInput = (props: CleverInputProps) => {
  const [focus, setFocus] = useState(false);
  const value = props.value ?? props.initialValue ?? '';
  const [inputValue, setInputValue] = useState<string>(value);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const resetTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  // Clear timeout when the component unmounts
  // If unmount before onBlur is executed, call onBlur
  useEffect(() => {
    return () => {
      resetTimeout();
      if (value !== inputValue && props.onBlur) {
        props.onBlur(inputValue);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Function to trigger update after the timeout, wrapped in useCallback
  const triggerUpdate = useCallback(() => {
    resetTimeout();
    timeoutRef.current = setTimeout(() => {
      if (!focus) {
        setInputValue(value);
      }
      resetTimeout();
    }, 3000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus]); // Dependencies: it depends on `focus`

  // When value changes from props, update the input value if not focused or after timeout
  useEffect(() => {
    if (!focus) {
      setInputValue(value); // Immediate update if not focused
    } else {
      triggerUpdate(); // Set up the timeout if editing
    }

    // Cleanup the timeout on unmount or when input changes
    return () => resetTimeout();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <Input
      {...props}
      value={inputValue}
      onChange={(val) => {
        // Ask parent to validate before committing the change to internal state.
        if (props.validateInput && !props.validateInput(val)) return;
        setInputValue(val);
        triggerUpdate();
        props.onChange && props.onChange(val);
      }}
      onBlur={(val) => {
        if (val !== inputValue) {
          setInputValue(val);
          props.onChange && props.onChange(val);
        }
        props.onBlur && props.onBlur(val);
        setFocus(false);
        triggerUpdate();
      }}
      onFocus={() => setFocus(true)}
    />
  );
};

export const Input = ({
  id,
  name,
  value,
  initialValue,
  placeholder,
  onChange,
  onChangeDebounced,
  onBlur,
  onBlurDebounced,
  onPressEnter,
  onPaste,
  onInput,
  maxValue,
  size = 'middle',
  prefix,
  suffix,
  bordered = false,
  disabled = false,
  readOnly = false,
  ellipsis,
  maxLength,
  color,
  passRef,
  className,
  showCount,
  onKeyPress,
  status,
  addonBefore,
  onFocus,
  addonAfter,
  allowClear,
  autoComplete,
  'data-cy': dataCy,
}: InputProps) => {
  const inputs = useInput<string, HTMLInputElement>({
    onChange,
    onChangeDebounced,
    onBlur,
    onBlurDebounced,
    onFocus,
    onPressEnter,
    maxValue,
  });

  return (
    <AntInput
      id={id}
      data-testid={id}
      name={name}
      value={value}
      defaultValue={initialValue}
      placeholder={placeholder}
      onChange={(e) => inputs.onChange(e.target.value, e)}
      onBlur={(e) => inputs.onBlur(e.target.value, e)}
      onFocus={(e) => inputs.onFocus(e.target.value, e)}
      onPressEnter={(event) => {
        const target = event.target as HTMLInputElement;
        if (onPressEnter) {
          inputs.onPressEnter(target.value, event);
        } else target.blur();
      }}
      onPaste={onPaste}
      size={size}
      bordered={bordered}
      prefix={prefix}
      suffix={suffix}
      disabled={disabled}
      readOnly={readOnly}
      style={{ textOverflow: ellipsis ? 'ellipsis' : undefined, color }}
      color={color}
      maxLength={maxLength}
      ref={passRef}
      spellCheck={false}
      className={className}
      onKeyDown={onKeyPress}
      showCount={showCount}
      status={status}
      addonAfter={addonAfter}
      addonBefore={addonBefore}
      data-cy={dataCy}
      onInput={onInput}
      allowClear={allowClear}
      autoComplete={autoComplete ?? (name ? 'on' : undefined)}
    />
  );
};
